import { create } from 'zustand';

/**
 * Canonical presence states matching the backend's UpdateUserDto enum:
 *   'online' | 'offline' | 'away' | 'busy'
 *
 * Anything the backend sends outside this set is normalised to 'offline' so
 * we never render a half-known state.
 */
export type PresenceStatus = 'online' | 'offline' | 'away' | 'busy';

export interface PresenceEntry {
  status: PresenceStatus;
  /** ISO timestamp the backend last reported activity for this user, if any. */
  lastSeen?: string;
  /** Local timestamp (ms) when we received this update — for stale-cleanup. */
  updatedAt: number;
}

interface PresenceState {
  /**
   * Keyed by user id. We use a plain object instead of a real Map so that
   * shallow-equality selectors work the way Zustand expects.
   */
  byUserId: Record<string, PresenceEntry>;
  setPresence: (userId: string, status: PresenceStatus, lastSeen?: string) => void;
  /** Hydrate many users at once (e.g. from a directory query). */
  hydrate: (entries: Array<{ id: string; presence?: string; lastSeen?: string }>) => void;
  /** Wipe everything — call on logout to avoid leaking presence into the next session. */
  clear: () => void;
}

const VALID: ReadonlySet<PresenceStatus> = new Set(['online', 'offline', 'away', 'busy']);

export function normalizeStatus(raw: unknown): PresenceStatus {
  return typeof raw === 'string' && (VALID as ReadonlySet<string>).has(raw)
    ? (raw as PresenceStatus)
    : 'offline';
}

export const usePresenceStore = create<PresenceState>((set) => ({
  byUserId: {},
  setPresence: (userId, status, lastSeen) =>
    set((state) => {
      const prev = state.byUserId[userId];
      // No-op if nothing changed — keeps selectors from re-rendering needlessly.
      if (prev && prev.status === status && prev.lastSeen === lastSeen) return state;
      return {
        byUserId: {
          ...state.byUserId,
          [userId]: { status, lastSeen, updatedAt: Date.now() },
        },
      };
    }),
  hydrate: (entries) =>
    set((state) => {
      let mutated = false;
      const next = { ...state.byUserId };
      const now = Date.now();
      for (const e of entries) {
        if (!e.id) continue;
        const status = normalizeStatus(e.presence);
        const prev = next[e.id];
        // Only overwrite if the existing entry is older or absent. Realtime
        // updates beat directory snapshots — this keeps a fresh socket event
        // from being clobbered by a slow refetch.
        if (!prev || prev.updatedAt < now - 30_000) {
          next[e.id] = { status, lastSeen: e.lastSeen, updatedAt: now };
          mutated = true;
        }
      }
      return mutated ? { byUserId: next } : state;
    }),
  clear: () => set({ byUserId: {} }),
}));

/**
 * Selector hook — reads ONE user's presence. Components that mount this hook
 * only re-render when that specific user's entry changes, not when any other
 * user's presence flips.
 */
export function usePresence(userId: string | undefined): PresenceStatus {
  return usePresenceStore((s) =>
    userId ? s.byUserId[userId]?.status ?? 'offline' : 'offline',
  );
}

/** Returns the last-seen ISO string for a user (or undefined if never reported). */
export function useLastSeen(userId: string | undefined): string | undefined {
  return usePresenceStore((s) => (userId ? s.byUserId[userId]?.lastSeen : undefined));
}
